import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["data-index-item-id"]
const _hoisted_2 = { class: "io-mfe-ig-index-list-item-desc" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { key: 0 }
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { key: 1 }

import { onUpdated, onMounted, ref, watch } from 'vue';
import { trucodeSeeMoreLink, useSearchStore } from 'stores/search-store';
import { storeToRefs } from 'pinia';
import { useGrouperStore } from 'stores/grouper-store';
import { Services } from 'src/services/service-manager';
import { BookViewRecord, SearchService } from 'src/services/search-service';
import { isEmpty } from 'src/common/arrayUtil';
import debounce from 'lodash.debounce';
import { CodeType } from 'src/contracts/codeType';
import { HEADERS } from 'src/components/search/index-code-table-headers';

export interface IndexRecord {
  id: number | string;
  label: string;
  content?: SeeMoreContent[];
  children: IndexRecord[];
  item?: BookViewRecord;
}

type SeeMoreContent = string | {
  content: string,
  options: string
}

const SCROLL_THRESHOLD = 150,
  RECORD_INCREMENT = 50;


export default /*#__PURE__*/_defineComponent({
  __name: 'IndexList',
  setup(__props) {

const treeRef = ref();
let minDataSequence: number, maxDataSequence: number;
const viewType = ref<string>();
const searchStore = useSearchStore();
const { searchResultDetails, initialItemRecord } =
  storeToRefs(searchStore);
const trucodeSearchService = Services.service(SearchService);
const grouperStore = useGrouperStore();

const expandedNodes = ref<Array<string | number>>([]);
let indexDiagnosisData = ref<IndexRecord[]>([]);
let mutableIndexData: IndexRecord[] = [];
let shouldScroll = false;

// This is entry point where click from the left search view performed
watch(
  searchResultDetails, (newSearchResults) => {
    parseNewResponse(newSearchResults);
  }
);

function parseNewResponse(newSearchResults: BookViewRecord[] | undefined) {
  mutableIndexData = [];
  expandedNodes.value = [];

  if (isEmpty(newSearchResults)) {
    return;
  }

  viewType.value = newSearchResults?.[0]?.data_book;
  // Refresh all data
  minDataSequence = initialItemRecord.value?.data_sequence || 0;
  maxDataSequence = initialItemRecord.value?.data_sequence || 0;

  if (viewType.value?.endsWith('INDEX')) {
    shouldScroll = true;
    parseResponseDiagnosisIndex(newSearchResults);
  }
}

onMounted(() => {
  if (!initialItemRecord.value) searchStore.focusIndexItem('A');
  if (!isEmpty(searchResultDetails.value))parseNewResponse(searchResultDetails.value)
});

// We should let data pass to vue and render, then perform scroll
onUpdated(() => {
  treeRef.value?.expandAll();
  if (shouldScroll) {
    setTimeout(() => {
      scrollToHighlight();
    });
    shouldScroll = false;
  }
});

function scrollToHighlight() {
  if (!initialItemRecord.value) return;

  const xpath = `//div[@data-index-item-id="${initialItemRecord.value?.data_id ?? initialItemRecord.value?.text}"]`;
  const matchingElement = document.evaluate(
    xpath,
    treeRef.value?.$el,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null
  ).singleNodeValue;
  matchingElement?.scrollIntoView();
}

const onScrollDiagnosisIndex = debounce((event) => {
  const el = event.target;

  if (el.scrollTop + el.offsetHeight + 100 > treeRef.value?.$el?.clientHeight) {
    maxDataSequence += RECORD_INCREMENT;
    trucodeSearchService
      .getCodebooksDetails(
        viewType.value,
        maxDataSequence,
        useGrouperStore().patientEffectiveDate
      )
      .then((resultDetails) => {
        parseResponseDiagnosisIndex(resultDetails);
      });
  } else if (el.scrollTop <= SCROLL_THRESHOLD && minDataSequence > 50) {
    el.scrollTop = SCROLL_THRESHOLD + 1;
    minDataSequence -= RECORD_INCREMENT;
    trucodeSearchService
      .getCodebooksDetails(
        viewType.value,
        minDataSequence,
        useGrouperStore().patientEffectiveDate
      )
      .then((resultDetails) => {
        parseResponseDiagnosisIndex(resultDetails);
      });
  }
}, 100);

function parseResponseDiagnosisIndex(records: BookViewRecord[] | undefined) {
  if (records === undefined) {
    console.log('Something went wrong with response from trucode.');
    return;
  }
  for (const record of records) {
    addRecord(record);
  }
  sortIndexDiagnosisData(mutableIndexData);
  indexDiagnosisData.value = [...mutableIndexData];
}

function addRecord(record: BookViewRecord) {
  let nodeList = mutableIndexData;
  if (record.record.type === 'letter') {
    nodeList.push({
      id: record.data_id,
      label: record.record.content,
      children: [],
      item: record
    });
    return;
  } else if (record.record.full_path === null) {
    console.log('Record full path is null and its not letter type');
    console.dir(record);
    return;
  }

  const terms = record.record.full_path.full.split(';');
  // Root of the tree, parent node here

  for (let i = 0; i < terms.length; i++) {
    const term = terms[i];

    let node = nodeList.find((x) => x.label === term);

    if (!node) {
      node = {
        id: term, // TODO: this id doesn't really work since labels can be repeated
        children: [],
        label: term,
      };
      nodeList.push(node);
    }

    // last element is associated with the record
    if (i == terms.length - 1) {
      node.id = record.data_id;
      node.item = record;

      // parse off content links
      const partialResults = record.record?.content?.match(trucodeSeeMoreLink);
      if (partialResults && partialResults.length > 2) {

        const parts = term.split(partialResults[2]);
        const contentArray: SeeMoreContent[] = [];
        contentArray.push(parts[0]);
        contentArray.push({
          content: partialResults[2],
          options: partialResults[1]
        });
        if (parts[1] && parts[1].trim()) {
          contentArray.push(parts[1]);
        }
        node.content = contentArray;
      }
    }

    nodeList = node.children;
  }
}

// Sort logic
// if both elements have data id's, sort by those
// if one element has a data id and the other does not, the element without the
// data id appears first
// if both elements don't have a data id (shouldn't be possible but who knows), then
// fallback to sorting by label
function sortIndexDiagnosisData(children: IndexRecord[]) {
  children.sort(function (a: IndexRecord, b: IndexRecord) {
    if (typeof(a.id) === 'number' && typeof(b.id) === 'number') {
      return a.id - b.id;
    } else if (typeof(a.id) === 'number') {
      return 1;
    } else if (typeof(b.id) === 'number') {
      return -1;
    } else {
      const textA = a.label.toUpperCase();
      const textB = b.label.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    }
  });
  for (const data of children) {
    if (data.children.length > 0) {
      sortIndexDiagnosisData(data.children);
    }
  }
}

function codeClicked(item: IndexRecord, code: string) {
  if (item?.item?.data_book) {
    switch (item?.item?.data_book) {
      case 'ICD10CM_DX_INDEX':
      case 'ICD10CM_E_INDEX':
        handleDxCodeClick(item, code);
        break;
      case 'ICD10PCS_PR_INDEX':
        handleProcedureCodeClick(item, code);
        break;
    }
  }
}

async function handleDxCodeClick(item: IndexRecord, code: string) {

  code = code.replace('-', '');
  // check if code is addable
  if (!!await searchStore.quickAddCodeSearch(code, CodeType.Diagnosis)) {
    grouperStore.addDiagnosisCode(code, undefined, undefined, true);
  } else {
    searchStore.focusIndexItem(code, 'Ref');
  }
}

function handleProcedureCodeClick(item: IndexRecord, code: string) {
  // if code is 7 characters, then add to grouper directly
  if (code) {
    if (code.length == 7) {
      grouperStore.addProcedureCode(code, undefined, true);
    } else {
      searchStore.setSelectedProcedureCode(code);
    }
  }
}

function seeMoreClicked(item: SeeMoreContent) {

  if (typeof item === 'string') {
    searchStore.focusIndexItem(item);
  } else {
    searchStore.focusIndexItem(item.content, item.options);
  }


}

function openTabular(e: any, code: string) {

  if (code) {
    e.preventDefault;
    searchStore.focusIndexItem(code, 'Ref');
  }
}


return (_ctx: any,_cache: any) => {
  const _component_q_tree = _resolveComponent("q-tree")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    id: "tree-holder",
    class: "iodine-scrollable-y browse-search full-height",
    onScroll: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onScrollDiagnosisIndex) && _unref(onScrollDiagnosisIndex)(...args)))
  }, [
    _createVNode(_component_q_tree, {
      nodes: _unref(indexDiagnosisData),
      "node-key": "id",
      "no-connectors": "",
      icon: "fa-solid fa-caret-right",
      class: "iodine-padding-left-lg",
      "default-expand-all": true,
      "no-transition": true,
      ref_key: "treeRef",
      ref: treeRef
    }, {
      "default-header": _withCtx((prop) => [
        _createElementVNode("div", {
          class: _normalizeClass(["row items-center iodine-padding-left-sm", {'io-mfe-ig-index-list-item-letter': prop?.node?.item?.record?.type == 'letter',
                      'io-mfe-ig-index-list-item-table-codes': prop?.node?.item?.record?.type == 'tablerow' || prop?.node?.item?.record?.type == 'tablerowchannelpublishing'}]),
          "data-index-item-id": prop.node.id || prop.node.label
        }, [
          _createElementVNode("div", _hoisted_2, [
            (!prop.node.content)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  onClick: _withModifiers((e) => openTabular(e, prop?.node?.item?.record?.regular_row_codes?.[0]), ["stop"])
                }, _toDisplayString(prop.node.label), 9, _hoisted_3))
              : (_openBlock(), _createElementBlock("span", {
                  key: 1,
                  onClick: _withModifiers((e) => openTabular(e, prop?.node?.item?.record?.regular_row_codes?.[0]), ["stop"])
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop.node.content, (content, index) => {
                    return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                      (typeof content === 'string')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString(content), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock("a", {
                            key: 1,
                            class: "io-mfe-ig-index-list-item-link",
                            onClick: _withModifiers(($event: any) => (seeMoreClicked(content)), ["stop"])
                          }, _toDisplayString(content.content), 9, _hoisted_5))
                    ], 64))
                  }), 128))
                ], 8, _hoisted_4)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop?.node?.item?.record?.regular_row_codes, (code) => {
              return (_openBlock(), _createElementBlock("a", {
                key: `${prop?.node?.id}-${code}`,
                class: "io-mfe-ig-index-list-item-code-link",
                onClick: _withModifiers(($event: any) => (codeClicked(prop.node, code)), ["stop"])
              }, _toDisplayString(code), 9, _hoisted_6))
            }), 128))
          ]),
          (prop?.node?.item?.record?.table_row_codes?.items
          && prop?.node?.id == _unref(initialItemRecord)?.data_sequence
          && _unref(HEADERS)[prop?.node?.item?.record?.table_row_codes?.table])
            ? (_openBlock(), _createElementBlock("table", _hoisted_7, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(HEADERS)[prop?.node?.item?.record?.table_row_codes?.table], (header, index) => {
                      return (_openBlock(), _createElementBlock("th", {
                        key: index,
                        innerHTML: header
                      }, null, 8, _hoisted_8))
                    }), 128))
                  ])
                ]),
                _createElementVNode("tbody", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop?.node?.item?.record?.table_row_codes?.items, (code, index) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: `${prop?.node?.id}-table-${index}`
                      }, [
                        (code?.[0])
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              class: "io-mfe-ig-index-list-item-code-link",
                              onClick: _withModifiers(($event: any) => (codeClicked(prop.node, code?.[0])), ["stop"])
                            }, _toDisplayString(code?.[0]), 9, _hoisted_9))
                          : (_openBlock(), _createElementBlock("span", _hoisted_10, "--"))
                      ]))
                    }), 128))
                  ])
                ])
              ]))
            : (_openBlock(), _createElementBlock("table", _hoisted_11, [
                _createElementVNode("tbody", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop?.node?.item?.record?.table_row_codes?.items, (code, index) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: `${prop?.node?.id}-table-${index}`
                      }, [
                        (code?.[0])
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              class: "io-mfe-ig-index-list-item-code-link",
                              onClick: _withModifiers(($event: any) => (codeClicked(prop.node, code?.[0])), ["stop"])
                            }, _toDisplayString(code?.[0]), 9, _hoisted_12))
                          : (_openBlock(), _createElementBlock("span", _hoisted_13, "--"))
                      ]))
                    }), 128))
                  ])
                ])
              ]))
        ], 10, _hoisted_1)
      ]),
      _: 1
    }, 8, ["nodes"])
  ], 544)), [
    [_vShow, viewType.value == 'ICD10CM_DX_INDEX']
  ])
}
}

})